import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Plus, Sparkles, User, WifiOff } from 'lucide-react';

import { fetchAcessiveis, fetchConfiguracao, type ConfiguracaoAcessivel } from '@/api/secretario';
import { PageHeader } from '@/components/ui/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/ErrorState';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthOpcional } from '@/context/AuthProvider';
import { classifyError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { setConfiguracaoAtual, useConfiguracaoAtual } from '@/lib/secretario/configuracaoAtual';

import { AtendimentosList } from './AtendimentosList';
import { CotaSecretarioContador, CotaSecretarioFaixa } from './CotaSecretarioFaixa';
import { PerfilDoAdvogado } from './PerfilDoAdvogado';
import { SecretarioWizard } from './SecretarioWizard';
import { SO_MEU, useEscoposDisponiveis } from './useEscoposDisponiveis';

/** De quem é o Secretário: é o que distingue um do outro — um por escritório, e o "só meu". */
function deQuemE(c: ConfiguracaoAcessivel): string {
  return c.escopo === 'PESSOAL' ? 'Só meu' : `Escritório ${c.escritorio_nome ?? ''}`.trim();
}

const PAPEL: Record<ConfiguracaoAcessivel['papel'], string> = {
  DONO: 'você configura',
  GESTOR: 'você gere',
  PARTICIPANTE: 'você participa',
};

/** O valor do seletor que abre a criação: ``novo:pessoal`` ou ``novo:<escritório>``. */
const NOVO = 'novo:';

/**
 * Portal do Secretário de IA (PLANO §9): wizard guiado de configuração e ativação.
 * A página resolve QUAL Secretário mostrar (o pessoal ou o de um escritório —
 * docs/PLANO_SECRETARIO_ESCRITORIO.md §6) e o que o usuário faz nele: quem gere
 * configura tudo; o advogado que só participa vê os atendimentos dele e o próprio
 * perfil.
 */
export default function SecretarioPage() {
  // ?tab=atendimentos abre direto na aba (o ?tab=conversas antigo continua valendo:
  // os e-mails já enviados apontam para ele).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParamBruto = searchParams.get('tab');
  const tabParam = tabParamBruto === 'conversas' ? 'atendimentos' : tabParamBruto;
  const user = useAuthOpcional()?.user;
  const selecionada = useConfiguracaoAtual();
  // Criando outro secretário: de quem (o escritório, ou "só meu"), já escolhido no seletor.
  const [criandoOutro, setCriandoOutro] = useState<string | null>(null);

  const acessiveis = useQuery({ queryKey: queryKeys.secretario.acessiveis, queryFn: fetchAcessiveis });
  const lista = acessiveis.data ?? [];
  // A escolha guardada que não existe mais (saiu do escritório, outro usuário no
  // aparelho) cai no primeiro do seletor.
  const valida = lista.some((c) => c.id === selecionada);
  const primeira = lista[0]?.id ?? null;
  const recarregando = acessiveis.isFetching;
  useEffect(() => {
    if (acessiveis.isSuccess && !recarregando && !valida) setConfiguracaoAtual(primeira);
  }, [acessiveis.isSuccess, recarregando, valida, primeira]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.secretario.config,
    queryFn: fetchConfiguracao,
    enabled: acessiveis.isSuccess && (valida || lista.length === 0),
  });
  const escopos = useEscoposDisponiveis(true);
  const podeCriarOutro = lista.length > 0 && (escopos.pessoal || escopos.doEscritorio.length > 0);
  const atual = lista.find((c) => c.id === selecionada);

  // O passo do número abre direto: é o link do aviso de desconexão (e-mail e push),
  // e o destino do "Reconectar" da faixa (cada clique é um pedido novo).
  const [pedidoPasso, setPedidoPasso] = useState(() => {
    const chave = searchParams.get('passo');
    return chave ? { chave, seq: 0 } : null;
  });
  // Passo atual do wizard, guardado aqui: criar a configuração remonta o wizard
  // (ele passa a viver dentro das abas) e ele precisa reabrir onde estava.
  const [passoAtual, setPassoAtual] = useState<string | null>(null);
  // Aplicado o pedido de passo (link ou "Reconectar"), ele é consumido: senão o
  // wizard remontado ao voltar da aba Atendimentos o reaplicaria, devolvendo o
  // advogado ao passo do link em vez de onde ele estava.
  const aoMudarPasso = (chave: string) => {
    setPassoAtual(chave);
    setPedidoPasso(null);
  };
  const reconectar = () => {
    setSearchParams({ tab: 'config', passo: 'canal' }, { replace: true });
    setPedidoPasso((atual) => ({ chave: 'canal', seq: (atual?.seq ?? 0) + 1 }));
  };
  const trocar = (valor: string) => {
    setPassoAtual(null);
    if (valor.startsWith(NOVO)) {
      setCriandoOutro(valor.slice(NOVO.length));
      return;
    }
    setCriandoOutro(null);
    setConfiguracaoAtual(valor);
  };

  if (acessiveis.isLoading || isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={32} label="Carregando configuração" />
      </div>
    );
  }

  const falha = acessiveis.error ?? error;
  if (falha) {
    const classified = classifyError(falha);
    return (
      <ErrorState
        title="Não foi possível carregar"
        description={classified.message}
        actions={[
          {
            label: 'Tentar novamente',
            onClick: () => {
              acessiveis.refetch();
              refetch();
            },
          },
        ]}
      />
    );
  }

  const config = criandoOutro !== null ? null : (data ?? null);
  const participante = config?.papel === 'PARTICIPANTE';
  const whatsappCaiu = !participante && config?.canal?.status === 'DESCONECTADO';

  // Um secretário por escritório, e o "só meu": o seletor, no canto do cabeçalho, diz de
  // quem é cada um e oferece criar o que ainda falta (docs/PLANO_SECRETARIO_ESCRITORIO.md §4.1).
  const seletor =
    lista.length > 1 || podeCriarOutro ? (
      <Select value={criandoOutro !== null ? `${NOVO}${criandoOutro}` : (atual?.id ?? '')} onValueChange={trocar}>
        <SelectTrigger
          className="h-auto min-h-11 w-56 py-1.5 text-left sm:w-64"
          aria-label="De quem é o secretário"
        >
          {criandoOutro !== null ? (
            <span className="flex min-w-0 items-center gap-2">
              <Plus className="h-4 w-4 shrink-0" />
              <span className="truncate">
                Novo{' '}
                {criandoOutro === SO_MEU
                  ? 'só meu'
                  : `do ${escopos.doEscritorio.find((e) => e.id === criandoOutro)?.nome ?? 'escritório'}`}
              </span>
            </span>
          ) : atual ? (
            <span className="flex min-w-0 items-center gap-2">
              {atual.escopo === 'PESSOAL' ? (
                <User className="h-4 w-4 shrink-0" />
              ) : (
                <Building2 className="h-4 w-4 shrink-0" />
              )}
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{deQuemE(atual)}</span>
                <span className="block truncate text-xs text-muted-foreground">{PAPEL[atual.papel]}</span>
              </span>
            </span>
          ) : (
            <span>Escolha o secretário</span>
          )}
        </SelectTrigger>
        <SelectContent align="end">
          <SelectGroup>
            <SelectLabel>Seus secretários</SelectLabel>
            {lista.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {deQuemE(c)} — {PAPEL[c.papel]}
              </SelectItem>
            ))}
          </SelectGroup>
          {podeCriarOutro && (
            <SelectGroup>
              <SelectLabel>Criar</SelectLabel>
              {escopos.doEscritorio.map((e) => (
                <SelectItem key={e.id} value={`${NOVO}${e.id}`}>
                  Secretário do escritório {e.nome}
                </SelectItem>
              ))}
              {escopos.pessoal && <SelectItem value={`${NOVO}${SO_MEU}`}>Um secretário só meu</SelectItem>}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
    ) : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Sparkles />}
        title="Secretário de IA"
        subtitle={
          criandoOutro !== null
            ? 'Um secretário novo: cada escritório tem o seu, e você pode ter também um só seu.'
            : config?.ativo
              ? 'Os atendimentos que o Secretário fez pelo WhatsApp e o que precisa de você.'
              : 'Configure e ative o atendimento automático dos seus clientes pelo WhatsApp.'
        }
        badge={
          config ? (
            <Badge variant={config.ativo ? 'default' : 'secondary'}>{config.ativo ? 'Ativo' : 'Inativo'}</Badge>
          ) : undefined
        }
        actions={seletor}
      />

      {config && !participante && <CotaSecretarioFaixa />}
      {whatsappCaiu && (
        <Alert variant="destructive">
          <WifiOff className="h-4 w-4" />
          <AlertTitle>Seu WhatsApp foi desconectado</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>O Secretário parou de atender. Leia o QR code de novo para voltar.</span>
            <Button type="button" size="sm" variant="outline" onClick={reconectar}>
              Reconectar
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {config && participante ? (
        // O advogado que só participa: os atendimentos dele e o perfil dele (§6, §4.2).
        <Tabs
          value={tabParam === 'perfil' ? 'perfil' : 'atendimentos'}
          onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })}
        >
          <TabsList>
            <TabsTrigger value="atendimentos">Meus atendimentos</TabsTrigger>
            <TabsTrigger value="perfil">Meu perfil</TabsTrigger>
          </TabsList>
          <TabsContent value="atendimentos" className="pt-4">
            <AtendimentosList key={config.id} config={config} />
          </TabsContent>
          <TabsContent value="perfil" className="pt-4">
            {/* Um por secretário: o que estava digitado num não vai para o outro. */}
            {user?.id && <PerfilDoAdvogado key={config.id} config={config} advogadoId={user.id} />}
          </TabsContent>
        </Tabs>
      ) : config ? (
        <Tabs
          // Com o Secretário ativo, o dia a dia é acompanhar os atendimentos — a
          // aba vem primeiro e é a default; a configuração vira secundária. O
          // deep link ?tab= sempre prevalece.
          value={
            tabParam === 'atendimentos' || tabParam === 'config'
              ? tabParam
              : config.ativo
                ? 'atendimentos'
                : 'config'
          }
          onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })}
        >
          {/* A contagem de triagens mora na linha das abas: no uso normal, sem linha própria. */}
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <TabsList>
            {config.ativo ? (
              <>
                <TabsTrigger value="atendimentos">Atendimentos</TabsTrigger>
                <TabsTrigger value="config">Configuração</TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="config">Configuração</TabsTrigger>
                <TabsTrigger value="atendimentos">Atendimentos</TabsTrigger>
              </>
            )}
          </TabsList>
          <CotaSecretarioContador />
          </div>
          <TabsContent value="config" className="pt-4">
            <SecretarioWizard
              // Um wizard por secretário: trocar no seletor não carrega o passo nem
              // o que estava digitado no outro.
              key={config.id}
              config={config}
              irParaPasso={pedidoPasso}
              passoInicial={passoAtual}
              onPassoChange={aoMudarPasso}
            />
          </TabsContent>
          <TabsContent value="atendimentos" className="pt-4">
            <AtendimentosList key={config.id} config={config} />
          </TabsContent>
        </Tabs>
      ) : (
        <SecretarioWizard
          // Um por escolha: o "de quem" vem do seletor (ou, no primeiro, do próprio passo).
          key={criandoOutro ?? 'primeiro'}
          config={null}
          escopoInicial={criandoOutro}
          onPassoChange={(chave) => {
            aoMudarPasso(chave);
            // Criado o secretário, a página volta a mostrar o escolhido (o novo).
            setCriandoOutro(null);
          }}
        />
      )}
    </div>
  );
}
